import React, { useState, useEffect } from 'react';
import { AlertTriangle, MessageSquare, ShieldBan, FileText, ShieldAlert } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { MarketingLayout } from '../components/shell/MarketingLayout';
import { LegalHero } from '../components/shell/LegalHero';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';

export const AupPage = () => {
  const { brand, colorMode, activeBrandId } = useTenant();
  const theme = activeBrandId;
  const isDark = colorMode === 'dark';
  const brandName = brand?.name || 'Our Platform';

  const proseClasses = cn(
    "prose prose-lg max-w-none leading-relaxed",
    "prose-headings:font-black prose-headings:tracking-tight prose-p:text-black prose-li:text-black prose-strong:text-black dark:prose-headings:text-white dark:prose-p:text-white dark:prose-li:text-white dark:prose-strong:text-white"
  );

  const [activeSection, setActiveSection] = useState('purpose');

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['purpose', 'prohibited', 'pulse-rules', 'enforcement'];
      let current = sections[0];
      for (const section of sections) {
        const el = document.getElementById(section);
        if (el) {
          const rect = el.getBoundingClientRect();
          // If the top of the section is at or above 150px from the top of the viewport
          if (rect.top <= 150) {
            current = section;
          }
        }
      }
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1];
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'purpose', label: '1. Purpose' },
    { id: 'prohibited', label: '2. Prohibited Activities' },
    { id: 'pulse-rules', label: '3. Pulse Engine Rules' },
    { id: 'enforcement', label: '4. Enforcement' },
  ];

  return (
    <MarketingLayout mainClassName="pt-28 pb-24 px-4 sm:px-6 lg:px-8">
      <SEO title="Acceptable Use Policy" description={`Read the Acceptable Use Policy (AUP) for ${brandName}.`} />
        <div className="max-w-6xl mx-auto">
          
          <LegalHero 
            title="Acceptable Use Policy"
            subtitle={`Guidelines for maintaining a safe, respectful, and lawful environment across all ${brandName} services and features.`}
            icon={<ShieldAlert size={14} />}
            label="Community Standards"
            version="1.0"
            updated="June 2026"
          />

          <div className="flex flex-col md:flex-row gap-12 lg:gap-24 relative">
            
            <aside className="md:w-64 flex-shrink-0 hidden md:block">
              <div className="sticky top-32 space-y-6">
                <h4 className={cn("font-bold uppercase text-[10px] tracking-widest font-mono", isDark ? "text-slate-500" : "text-slate-400")}>
                  On this page
                </h4>
                <nav className="flex flex-col gap-3 relative before:absolute before:inset-y-0 before:left-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800">
                  {navItems.map((item) => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`} 
                      className={cn(
                        "pl-4 py-1 text-sm font-medium transition-all duration-200 relative",
                        activeSection === item.id 
                          ? "text-brand-primary before:absolute before:inset-y-0 before:-left-px before:w-[2px] before:bg-brand-primary" 
                          : isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
                      )}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 max-w-3xl space-y-20">
              
              <section id="purpose" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary">
                    <FileText size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">1. Purpose</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    This Acceptable Use Policy ("AUP") outlines the permissible and prohibited behaviors while using {brandName}. This policy works in tandem with our Terms of Service to ensure our infrastructure—especially our Digital Vault and Pulse Engine—is not used for malicious, illegal, or abusive activities.
                  </p>
                </div>
              </section>

              <section id="prohibited" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center text-rose-500">
                    <AlertTriangle size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">2. Prohibited Activities</h2>
                </div>
                <div className={proseClasses}>
                  <p className="mb-4">You agree not to use the Service to:</p>
                  <ul className={cn("list-none space-y-4 pl-0 p-6 rounded-xl border mt-6", isDark ? "bg-[#111] border-white/10" : "bg-white border-gray-100 shadow-sm")}>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                      <span><strong className={isDark ? "text-white" : "text-slate-900"}>Illegal Acts:</strong> Violate any applicable local, state, national, or international law, including laws governing intellectual property and privacy.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                      <span><strong className={isDark ? "text-white" : "text-slate-900"}>Harmful Content:</strong> Upload to the Digital Vault any material that contains software viruses, malware, or any other code designed to disrupt or damage computer software or hardware.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                      <span><strong className={isDark ? "text-white" : "text-slate-900"}>Abuse & Harassment:</strong> Transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, or invasive of another's privacy.</span>
                    </li>
                  </ul>
                </div>
              </section>

              <section id="pulse-rules" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <MessageSquare size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">3. Pulse Engine (Broadcast) Rules</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    {brandName} provides powerful communication tools, including the Pulse Engine for SMS and email broadcasts. To comply with anti-spam legislation (like CAN-SPAM and TCPA), you MUST:
                  </p>
                  <ul className="list-disc pl-5 mt-4 marker:text-blue-500">
                    <li>Only send messages to recipients who have explicitly opted-in or provided consent to receive communications from your organization.</li>
                    <li>Never use the Pulse Engine to send unsolicited marketing materials, phishing scams, or pyramid schemes.</li>
                    <li>Always respect opt-out and unsubscribe requests immediately.</li>
                  </ul>
                </div>
              </section>

              <section id="enforcement" className="scroll-mt-32">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-500">
                    <ShieldBan size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-display">4. Enforcement</h2>
                </div>
                <div className={proseClasses}>
                  <p>
                    We reserve the right to investigate and take appropriate legal action against anyone who violates this AUP. Actions, taken at our sole discretion, may include, but are not limited to:
                  </p>
                  <ul className="list-disc pl-5 mt-4 marker:text-teal-500">
                    <li>Immediate removal of offending content from the Digital Vault.</li>
                    <li>Immediate suspension or termination of your account without a refund.</li>
                    <li>Reporting the behavior to law enforcement authorities.</li>
                  </ul>
                </div>
              </section>

            </div>
          </div>
        </div>
    </MarketingLayout>
  );
};
